import { useCallback, useEffect, useRef, useState } from 'react'
import type { ComponentStyles } from '@/components/cms/StyleCustomizer'
import type { CanvasBlock } from '@/lib/cms/card-registry'
import type { LayoutSection } from '@/types/database.types'

/* ============================================================
   Preview Bridge — PRD §8.1 CMS postMessage communication
   Two-sided hook for admin ↔ preview iframe communication.
   Supports: content edits, section scrolling, style changes,
   layout updates (add/remove/reorder sections + variants).
   ============================================================ */

// ── Message Protocol ──────────────────────────────────────────

export type PreviewMessage =
  | { type: 'CONTENT_UPDATE'; section: string; key: string; value: string }
  | { type: 'SECTION_SCROLL'; section: string }
  | { type: 'SAVE_COMPLETE' }
  | { type: 'DRAFT_READY' }
  | { type: 'STYLE_UPDATE'; sectionId: string | null; styles: Partial<ComponentStyles> }
  | { type: 'LAYOUT_UPDATE'; sections: LayoutSection[] }
  | { type: 'CANVAS_UPDATE'; blocks: CanvasBlock[] }

function getWindowOrigin() {
  return typeof window !== 'undefined' ? window.location.origin : ''
}

function getIframeTargetOrigin(iframe: HTMLIFrameElement | null): string {
  const fallback = getWindowOrigin()
  if (!iframe) return fallback

  const getAttribute =
    typeof (iframe as { getAttribute?: unknown }).getAttribute === 'function'
      ? ((iframe as { getAttribute: (name: string) => string | null }).getAttribute('src') ?? '')
      : ''
  const src = getAttribute || iframe.src
  if (!src) return fallback

  try {
    return new URL(src, fallback).origin
  } catch {
    return fallback
  }
}

// ── Admin Side: Send messages to iframe ───────────────────────

export function usePreviewSender(iframeRef: React.RefObject<HTMLIFrameElement | null>) {
  const send = useCallback(
    (msg: PreviewMessage) => {
      const iframe = iframeRef.current
      if (!iframe?.contentWindow) return
      iframe.contentWindow.postMessage(msg, getIframeTargetOrigin(iframe))
    },
    [iframeRef]
  )

  const sendContentUpdate = useCallback(
    (section: string, key: string, value: string) => {
      send({ type: 'CONTENT_UPDATE', section, key, value })
    },
    [send]
  )

  const sendSectionScroll = useCallback(
    (section: string) => {
      send({ type: 'SECTION_SCROLL', section })
    },
    [send]
  )

  const sendSaveComplete = useCallback(() => {
    send({ type: 'SAVE_COMPLETE' })
  }, [send])

  const sendStyleUpdate = useCallback(
    (sectionId: string | null, styles: Partial<ComponentStyles>) => {
      send({ type: 'STYLE_UPDATE', sectionId, styles })
    },
    [send]
  )

  const sendLayoutUpdate = useCallback(
    (sections: LayoutSection[]) => {
      send({ type: 'LAYOUT_UPDATE', sections })
    },
    [send]
  )

  const sendCanvasUpdate = useCallback(
    (blocks: CanvasBlock[]) => {
      send({ type: 'CANVAS_UPDATE', blocks })
    },
    [send]
  )

  return {
    send,
    sendContentUpdate,
    sendSectionScroll,
    sendSaveComplete,
    sendStyleUpdate,
    sendLayoutUpdate,
    sendCanvasUpdate,
  }
}

// ── Preview Side: Receive messages from admin ─────────────────

export type DraftOverrides = Record<string, Record<string, string>>

export function usePreviewReceiver() {
  const [overrides, setOverrides] = useState<DraftOverrides>({})
  const [isReady] = useState(true)
  const [liveStyles, setLiveStyles] = useState<{
    sectionId: string | null
    styles: Partial<ComponentStyles>
  } | null>(null)
  const [liveLayout, setLiveLayout] = useState<LayoutSection[] | null>(null)
  const [liveCanvasBlocks, setLiveCanvasBlocks] = useState<CanvasBlock[] | null>(null)
  const scrollCallbackRef = useRef<((section: string) => void) | null>(null)

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Security: only accept messages from same origin
      if (event.origin !== getWindowOrigin()) return

      const msg = event.data as PreviewMessage
      if (!msg || typeof msg !== 'object' || !msg.type) return

      switch (msg.type) {
        case 'CONTENT_UPDATE':
          setOverrides((prev) => ({
            ...prev,
            [msg.section]: {
              ...(prev[msg.section] || {}),
              [msg.key]: msg.value,
            },
          }))
          break

        case 'SECTION_SCROLL':
          scrollCallbackRef.current?.(msg.section)
          break

        case 'SAVE_COMPLETE':
          // Clear overrides — DB is now the source of truth
          setOverrides({})
          setLiveStyles(null)
          setLiveLayout(null)
          setLiveCanvasBlocks(null)
          break

        case 'STYLE_UPDATE':
          setLiveStyles({ sectionId: msg.sectionId, styles: msg.styles })
          break

        case 'LAYOUT_UPDATE':
          setLiveLayout(msg.sections)
          break

        case 'CANVAS_UPDATE':
          setLiveCanvasBlocks(msg.blocks)
          break
      }
    }

    window.addEventListener('message', handleMessage)

    // Notify parent that draft mode is ready
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'DRAFT_READY' } satisfies PreviewMessage, getWindowOrigin())
    }

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const onSectionScroll = useCallback((cb: (section: string) => void) => {
    scrollCallbackRef.current = cb
  }, [])

  /** Merge draft overrides into DB-loaded content */
  const mergeContent = useCallback(
    (section: string, dbContent: Record<string, string>): Record<string, string> => {
      const sectionOverrides = overrides[section]
      if (!sectionOverrides) return dbContent
      return { ...dbContent, ...sectionOverrides }
    },
    [overrides]
  )

  return {
    overrides,
    isReady,
    mergeContent,
    onSectionScroll,
    liveStyles,
    liveLayout,
    liveCanvasBlocks,
  }
}
